import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  contractStatusConverter,
  getContracts,
  getHideWelcomeScreen,
  getLegalTexts,
  getPermissions,
  getPersonalData,
  getTicketSettings,
  GetUnread,
  getUnread,
  ImmomioIconName,
  IUserData,
  LoadLegalTexts,
  RequestLogout,
  setHideWelcomeScreen,
} from '@resident-nx/shared';
import {
  FooterComponent,
  FooterLink,
  footerNavItems,
  hasLegalText,
  HeaderComponent,
  IdaChatbotWebComponent,
  MobileNavigationComponent,
  NavigationAlignment,
  NavigationComponent,
  NavigationItem,
  NavigationItemStateEnum,
  ROUTE,
} from '@resident-nx/web';
import {
  AvatarSizeEnum,
  AvatarWebComponent,
  ImpersonationBannerComponent,
} from '@resident-nx/web/ui';
import { ENDPOINTS } from '../../../../../endpoints';
import { AsyncPipe, NgClass } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { KeycloakService, LocalStorageService } from '@resident-nx/web/data-access';

const navigationItems = [
  {
    name: 'dashboard',
    link: ROUTE.DASHBOARD,
    label: 'main_navigation_dashboard_l',
    icon: ImmomioIconName.Dashboard,
  },
  {
    name: 'news',
    link: ROUTE.NEWS,
    label: 'main_navigation_news_l',
    icon: ImmomioIconName.News,
  },
  {
    name: 'tickets',
    link: ROUTE.TICKETS,
    label: 'main_navigation_concerns_l',
    icon: ImmomioIconName.Ticket,
  },
  {
    name: 'home',
    link: ROUTE.HOME,
    label: 'main_navigation_my_home_l',
    icon: ImmomioIconName.House,
  },
];
const footerItems = footerNavItems;

const profileSettingsItems = [
  {
    name: 'profile page',
    link: ROUTE.PROFILE_PAGE,
    label: 'user.personal_data',
    icon: ImmomioIconName.User,
  },
  {
    name: 'payment-information',
    link: ROUTE.PAYMENT_INFORMATION,
    label: 'user_profile.payment_information_l',
    icon: ImmomioIconName.PaymentRequest,
  },
  {
    name: 'cooperative-information',
    link: ROUTE.COOPERATIVE_INFORMATION,
    label: 'account.cooperative_data',
    icon: ImmomioIconName.Application,
  },
  {
    name: 'account',
    link: `${ENDPOINTS.SETTINGS}/${ENDPOINTS.ACCOUNT}`,
    label: 'main_navigation.settings_l',
    icon: ImmomioIconName.Gear,
  },
  {
    name: 'notifications',
    link: `${ENDPOINTS.SETTINGS}/${ENDPOINTS.NOTIFICATIONS}`,
    label: 'main_navigation.notifications_l',
    icon: ImmomioIconName.News,
  },
  {
    name: 'Faq',
    link: ROUTE.FAQ,
    label: 'main_navigation.user_faq_l',
    icon: ImmomioIconName.QuestionOutlined,
  },
  {
    name: 'logout',
    link: ENDPOINTS.LOGOUT,
    label: 'Logout',
    icon: ImmomioIconName.Logout,
    state: NavigationItemStateEnum.WARNING,
  },
];

@Component({
  selector: 'rs-web-authenticated',
  templateUrl: './authenticated.component.html',
  styleUrls: ['./authenticated.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterOutlet,
    AsyncPipe,
    HeaderComponent,
    NavigationComponent,
    AvatarWebComponent,
    FooterComponent,
    MobileNavigationComponent,
    NavigationComponent,
    NgClass,
    NavigationComponent,
    IdaChatbotWebComponent,
    ImpersonationBannerComponent,
  ],
})
export class AuthenticatedComponent implements OnInit {
  #destroyRef = inject(DestroyRef);
  public navigationItems = signal<NavigationItem[]>([]);
  public profileSettingsItems: NavigationItem[];
  public footerItems: FooterLink[];
  public profileSettingsOpen = false;
  // This sets the profile settings navigation to display: none or block
  // It's required to not render this space, because in the app-tab-navigation component it will be scrolled in to the view.
  public showProfileSettingsNavigationInTemplate = false;
  public navigationAlignment = NavigationAlignment;
  public userData$: Observable<IUserData>;
  public avatarSize = AvatarSizeEnum;
  public permissions = this.store.selectSignal(getPermissions);
  // Read-only support mode: a landlord impersonating a resident sees a banner
  // and cannot write (writes are rejected by the backend).
  #keycloakService = inject(KeycloakService);
  public isImpersonating = this.#keycloakService.isImpersonating;
  public impersonationExpiresAt = this.#keycloakService.tokenExpiresAt;
  private localStorageService = inject(LocalStorageService);

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.store.dispatch(
      setHideWelcomeScreen({
        hideWelcomeScreen: this.localStorageService.getItem('hideWelcomeScreen') === true,
      })
    );
    this.store.dispatch(LoadLegalTexts());
    this.store.dispatch(GetUnread());

    combineLatest([
      this.store.select(getContracts),
      this.store.select(getTicketSettings),
      this.store.select(getHideWelcomeScreen),
      this.store.select(getLegalTexts),
      this.store.select(getUnread),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([contracts, ticketSettings, hideDashboardNavigation, legalTexts, unread]) => {
        const ticketCreationWithoutContractAllowed =
          ticketSettings.allowContractlessTicketCreationForDamages ||
          ticketSettings.allowContractlessTicketCreationForOtherConcerns;

        const areAllContractsExpired = contractStatusConverter.allContractsExpired(contracts);
        const hasValidContracts = contracts.some(c => c.contractStartTime);
        const showTicketNavigationItem =
          (hasValidContracts && !areAllContractsExpired) || ticketCreationWithoutContractAllowed;

        this.navigationItems.set(
          showTicketNavigationItem
            ? navigationItems
            : navigationItems.filter(item => item.name !== ROUTE.TICKETS)
        );

        this.navigationItems.update(items =>
          items.filter(item => {
            if (item.link === ROUTE.DASHBOARD && hideDashboardNavigation) {
              return false;
            }
            return true;
          })
        );

        const hasUnreadTickets =
          unread?.ticketsWithUnreadMessages?.damage > 0 ||
          unread?.ticketsWithUnreadMessages?.concern > 0;

        this.navigationItems.update(items =>
          items.map(tab => {
            if (tab.name === 'home') {
              return { ...tab, showNotification: !!unread?.documents };
            }
            if (tab.name === 'tickets') {
              return { ...tab, showNotification: hasUnreadTickets };
            }
            return tab;
          })
        );

        this.footerItems = footerItems.filter(item => hasLegalText(legalTexts?.[item.link]));
      });

    this.filterProfileSettingsItems();
  }

  // The impersonation token is static, so an expired countdown means every
  // further request would fail — end the session right away.
  public onImpersonationExpired(): void {
    this.store.dispatch(RequestLogout({}));
  }

  public onProfileSettingsToggleClick(event: Event) {
    event.stopPropagation();

    if (!this.profileSettingsOpen) this.showProfileSettingsNavigationInTemplate = true;

    this.profileSettingsOpen = !this.profileSettingsOpen;
  }

  private filterProfileSettingsItems(): void {
    this.userData$ = this.store.select(getPersonalData);
    combineLatest([this.store.select(getContracts), this.userData$])
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map(([contracts, userData]) => {
          const showPaymentButton = !(
            contracts.length === 0 || contractStatusConverter.allContractsExpired(contracts)
          );
          let filteredItems = [...profileSettingsItems];
          if (!showPaymentButton) {
            filteredItems = filteredItems.filter(item => item.name !== 'payment-information');
          }
          const cooperativeData = userData?.cooperativeData;
          const hasCooperativeData =
            cooperativeData &&
            (cooperativeData.membershipNumber ||
              cooperativeData.entryDate ||
              cooperativeData.leavingDate);
          if (!hasCooperativeData) {
            filteredItems = filteredItems.filter(item => item.name !== 'cooperative-information');
          }

          return filteredItems;
        })
      )
      .subscribe(items => {
        this.profileSettingsItems = items;
      });
  }
}
